// Empire Apps Chassis · connectors/financial/ais-tis
// Income Tax AIS (Annual Information Statement) + TIS (Taxpayer Information Summary)
// JSON or PDF upload. Parses gross salary, TDS, interest, dividends, capital gains.
//
// AIS JSON schema reference: incometax.gov.in/iec/foportal v1.4 (AY 2024-25)
// PDF fallback via Adobe PDF bridge.

import {
  type AisTisExtract,
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  fail,
  isoTs,
  ok,
  paise,
  pan,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge, RE_INR, RE_PAN } from './adobe-pdf-bridge.ts';

const STATUS_KEY = 'empire:financial:ais-tis:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly pan: string | null;
  readonly ay: string | null;
  readonly lastSync: string | null;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, pan: null, ay: null, lastSync: null });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

interface AisJsonShape {
  readonly Pan?: string;
  readonly AssessmentYear?: string;
  readonly Salary?: { readonly GrossSalary?: number };
  readonly TDS?: { readonly TotalTDS?: number };
  readonly Interest?: { readonly TotalInterest?: number };
  readonly Dividends?: { readonly TotalDividend?: number };
  readonly Trades?: {
    readonly StockGrossTurnover?: number;
    readonly StockNetTurnover?: number;
    readonly MfRedemptions?: number;
  };
  readonly CapitalGains?: {
    readonly LTCG?: number;
    readonly STCG?: number;
  };
}

export function parseAisJson(text: string): AisTisExtract | null {
  let j: AisJsonShape;
  try {
    j = JSON.parse(text) as AisJsonShape;
  } catch {
    return null;
  }
  const panStr = j.Pan;
  if (!panStr) return null;
  return {
    pan: pan(panStr),
    ay: j.AssessmentYear ?? 'AY 2024-25',
    grossSalaryPaise: paise((j.Salary?.GrossSalary ?? 0) * 100),
    tdsTotalPaise: paise((j.TDS?.TotalTDS ?? 0) * 100),
    interestIncomePaise: paise((j.Interest?.TotalInterest ?? 0) * 100),
    dividendsPaise: paise((j.Dividends?.TotalDividend ?? 0) * 100),
    stockTradesGrossPaise: paise((j.Trades?.StockGrossTurnover ?? 0) * 100),
    stockTradesNetPaise: paise((j.Trades?.StockNetTurnover ?? 0) * 100),
    mfRedemptionsPaise: paise((j.Trades?.MfRedemptions ?? 0) * 100),
    capitalGainsLtcgPaise: paise((j.CapitalGains?.LTCG ?? 0) * 100),
    capitalGainsStcgPaise: paise((j.CapitalGains?.STCG ?? 0) * 100),
    asOf: isoTs(),
  };
}

export function parseAisPdfText(text: string): AisTisExtract | null {
  const panMatch = text.match(RE_PAN);
  if (!panMatch) return null;
  const amounts: number[] = [];
  for (const m of text.matchAll(RE_INR)) {
    const n = Number(m[1]?.replace(/,/g, '') ?? 'NaN');
    if (Number.isFinite(n)) amounts.push(Math.round(n * 100));
  }
  RE_INR.lastIndex = 0;
  const ayMatch = text.match(/AY\s+(\d{4}[-/]\d{2,4})/);
  const tagged = (label: RegExp, fallback = 0): number => {
    const idx = text.search(label);
    if (idx === -1) return fallback;
    const slice = text.slice(idx, idx + 200);
    const m = slice.match(RE_INR);
    if (!m) return fallback;
    return Math.round(Number(m[1]?.replace(/,/g, '') ?? '0') * 100);
  };
  return {
    pan: pan(panMatch[0]),
    ay: ayMatch?.[1] ?? 'AY 2024-25',
    grossSalaryPaise: paise(tagged(/Gross Salary|Salary/i)),
    tdsTotalPaise: paise(tagged(/Total\s+TDS/i)),
    interestIncomePaise: paise(tagged(/Interest\s+(?:Income|from)/i)),
    dividendsPaise: paise(tagged(/Dividend/i)),
    stockTradesGrossPaise: paise(tagged(/Stock\s+(?:Gross|Turnover)/i)),
    stockTradesNetPaise: paise(tagged(/Stock\s+Net/i)),
    mfRedemptionsPaise: paise(tagged(/Mutual\s+Fund\s+(?:Redemption|Sale)/i)),
    capitalGainsLtcgPaise: paise(tagged(/Long[\s-]+Term\s+Capital\s+Gains?/i)),
    capitalGainsStcgPaise: paise(tagged(/Short[\s-]+Term\s+Capital\s+Gains?/i)),
    asOf: isoTs(),
  };
}

function mockExtract(): AisTisExtract {
  return {
    pan: pan('ABCDE1234F'),
    ay: 'AY 2025-26',
    grossSalaryPaise: paise(285000000),
    tdsTotalPaise: paise(45200000),
    interestIncomePaise: paise(8200000),
    dividendsPaise: paise(1850000),
    stockTradesGrossPaise: paise(348000000),
    stockTradesNetPaise: paise(58500000),
    mfRedemptionsPaise: paise(125000000),
    capitalGainsLtcgPaise: paise(12500000),
    capitalGainsStcgPaise: paise(2850000),
    asOf: isoTs(),
  };
}

export const aisTis: Connector = {
  id: 'ais-tis',
  displayName: 'Income Tax AIS / TIS upload',
  category: 'tax',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.connected ? 1 : 0,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-ais-upload', `AIS+TIS ${opts?.ay ?? 'AY 2025-26'}`);
    const connectedAt = isoTs();
    writeStatus({ connected: true, pan: opts?.pan ?? null, ay: opts?.ay ?? null, lastSync: connectedAt });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (opts?.fileBlob) {
      const buf = await opts.fileBlob.text();
      if (buf.startsWith('{')) {
        const parsed = parseAisJson(buf);
        if (parsed) {
          writeStatus({ ...s, lastSync: isoTs(), pan: parsed.pan, ay: parsed.ay });
          return ok({ ais: parsed });
        }
      }
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const parsed = parseAisPdfText(extract.textFull);
      if (parsed) {
        writeStatus({ ...s, lastSync: isoTs(), pan: parsed.pan, ay: parsed.ay });
        return ok({ ais: parsed });
      }
    }
    const m = mockExtract();
    writeStatus({ ...s, lastSync: isoTs(), pan: m.pan, ay: m.ay });
    return ok({ ais: m });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, pan: null, ay: null, lastSync: null });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-ais-upload' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { ais: mockExtract() };
  },
};
